<template>
    <Head>
        <title>Blockchain: Stake {{ network }} [{{ address }}]</title>
    </Head>

    <authenticated-layout :notifications="notifications" >
        <div class="flex flex-col gap-8 p-8">
            <h1 class="text-2xl font-bold">{{ address }}</h1>

            <div v-if="details" class="grid grid-cols-3 gap-8">

                <div class="flex flex-col border rounded bg-white gap-4 p-4">
                    <div class="flex flex-row justify-between items-baseline hover:bg-gray-100">
                        <v-label>Free balance</v-label>
                        <strong class="font-mono">{{ details.freeBalance }}</strong>
                    </div>
                    <div class="flex flex-row justify-between items-baseline hover:bg-gray-100">
                        <v-label>Frozen balance</v-label>
                        <strong class="font-mono">{{ details.frozenBalance }}</strong>
                    </div>
                    <div class="flex flex-row justify-between items-baseline hover:bg-gray-100">
                        <v-label>Total balance</v-label>
                        <strong class="font-mono">{{ details.totalBalance }}</strong>
                    </div>
                    <div class="flex flex-row justify-between items-baseline hover:bg-gray-100">
                        <v-label>Total energy</v-label>
                        <strong class="font-mono">
                            {{ details.totalEnergy - details.usedEnergy }} /
                            <span class="text-red-700">{{details.totalEnergy}}</span>
                        </strong>
                    </div>
                    <form method="post" class="flex flex-row gap-4 items-end" @submit.prevent="getStakingAnalysis">
                        <v-input type="date" label="From" v-model="stakingAnalysis.start"/>
                        <v-input type="date" label="To" v-model="stakingAnalysis.end"/>
                        <v-button>Get Analysis</v-button>
                    </form>
                </div>

                <v-modal :showing="stakingAnalysis.show" @close="stakingAnalysis.show = false">
                    <template v-slot:header>
                        Staking Analysis {{ address }}
                        <p>{{ moment(stakingAnalysis.start).startOf('day').format('L LTS') }} - {{ moment(stakingAnalysis.end).endOf('day').format('L LTS') }}</p>
                    </template>

                    <div class="flex flex-col">
                        <div class="flex flex-row justify-between items-baseline hover:bg-gray-100 p-1">
                            <v-label>Top Ups</v-label>
                            <strong class="font-mono">{{ stakingAnalysis.result.topUps }}</strong>
                        </div>
                        <div class="flex flex-row justify-between items-baseline hover:bg-gray-100 p-1">
                            <v-label>Sent TRX</v-label>
                            <strong class="font-mono">{{ stakingAnalysis.result.sentTRX }}</strong>
                        </div>
                        <div
                            v-if="stakingAnalysis.result.trxTransactions && stakingAnalysis.result.trxTransactions.length"
                            class="flex flex-row justify-between items-baseline hover:bg-gray-100 p-1 cursor-pointer"
                            @click="stakingAnalysis.result.showTransactions = !stakingAnalysis.result.showTransactions"
                        >
                            <v-label>TRX Transactions</v-label>
                            <span :class="stakingAnalysis.result.showTransactions ? 'text-red-700' : 'text-green-700'">{{ stakingAnalysis.result.showTransactions ? '-' : '+' }}</span>
                        </div>
                        <div class="flex flex-col text-sm" v-if="stakingAnalysis.result.showTransactions">
                            <a class="text-blue-700 hover:bg-gray-100" :href="`https://tronscan.org/#/transaction/${tx}`" target="_blank" v-for="tx of stakingAnalysis.result.trxTransactions">{{ tx }}</a>
                        </div>
                        <div class="flex flex-row justify-between items-baseline hover:bg-gray-100 p-1">
                            <v-label>Sending Fee</v-label>
                            <strong class="font-mono">{{ stakingAnalysis.result.sendingFee }}</strong>
                        </div>
                        <div class="flex flex-row justify-between items-baseline hover:bg-gray-100 p-1">
                            <v-label>Delegations</v-label>
                            <strong class="font-mono">{{ stakingAnalysis.result.delegations }}</strong>
                        </div>
                        <div class="flex flex-row justify-between items-baseline hover:bg-gray-100 p-1">
                            <v-label>Delegate Fee</v-label>
                            <strong class="font-mono">{{ stakingAnalysis.result.delegateFee }}</strong>
                        </div>
                        <div
                            v-if="stakingAnalysis.result.delegateTransactions && stakingAnalysis.result.delegateTransactions.length"
                            class="flex flex-row justify-between items-baseline hover:bg-gray-100 p-1 cursor-pointer"
                            @click="stakingAnalysis.result.showDelegateTransactions = !stakingAnalysis.result.showDelegateTransactions"
                        >
                            <v-label>Delegate Transactions</v-label>
                            <span :class="stakingAnalysis.result.showDelegateTransactions ? 'text-red-700' : 'text-green-700'">{{ stakingAnalysis.result.showDelegateTransactions ? '-' : '+' }}</span>
                        </div>
                        <div class="flex flex-col gap-2" v-if="stakingAnalysis.result.showDelegateTransactions">
                            <div class="flex flex-col text-sm" v-for="tx of stakingAnalysis.result.delegateTransactions">
                                <div class="flex flex-col hover:bg-gray-100">
                                    <v-label>Delegation</v-label>
                                    <a class="text-blue-700" :href="`https://tronscan.org/#/transaction/${tx.delegationTransaction}`" target="_blank" >{{ tx.delegationTransaction }}</a>
                                </div>
                                <div class="flex flex-col pl-6 hover:bg-gray-100" v-show="tx.withdrawalTransaction">
                                    <v-label>Withdrawal</v-label>
                                    <a class="text-blue-700" :href="`https://tronscan.org/#/transaction/${tx.withdrawalTransaction}`" target="_blank" >{{ tx.withdrawalTransaction }}</a>
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-row justify-between items-baseline hover:bg-gray-100 p-1">
                            <v-label>Reclaim Fee</v-label>
                            <strong class="font-mono">{{ stakingAnalysis.result.reclaimFee }}</strong>
                        </div>

                        <div
                            v-if="stakingAnalysis.result.reclaimTransactions && stakingAnalysis.result.reclaimTransactions.length"
                            class="flex flex-row justify-between items-baseline hover:bg-gray-100 p-1 cursor-pointer"
                            @click="stakingAnalysis.result.showReclaimTransactions = !stakingAnalysis.result.showReclaimTransactions"
                        >
                            <v-label>Reclaim Transactions</v-label>
                            <span :class="stakingAnalysis.result.showReclaimTransactions ? 'text-red-700' : 'text-green-700'">{{ stakingAnalysis.result.showReclaimTransactions ? '-' : '+' }}</span>
                        </div>
                        <div class="flex flex-col text-sm" v-if="stakingAnalysis.result.showReclaimTransactions">
                            <a class="text-blue-700 hover:bg-gray-100" :href="`https://tronscan.org/#/transaction/${tx}`" target="_blank" v-for="tx of stakingAnalysis.result.reclaimTransactions">{{ tx }}</a>
                        </div>

                        <div class="flex flex-row justify-between items-baseline hover:bg-gray-100 p-1">
                            <v-label>Rewards</v-label>
                            <strong class="font-mono">{{ stakingAnalysis.result.rewards }}</strong>
                        </div>

                        <div class="flex flex-row justify-between items-baseline hover:bg-gray-100 p-1">
                            <v-label>Saved</v-label>
                            <strong class="font-mono">{{ stakingAnalysis.result.saved }}</strong>
                        </div>
                    </div>

                    <template v-slot:footer>
                        <v-button type="button" @click="stakingAnalysis.show = false">Close</v-button>
                    </template>
                </v-modal>

                <div class="flex flex-col border rounded bg-white gap-4 p-4">
                    <div class="flex flex-row justify-between items-baseline">
                        <p class="text-lg font-semibold">Resources</p>
                    </div>
                    <div class="my-auto grid grid-cols-2 items-center">
                        <div class="flex flex-col items-center gap-2">
                            <v-label>Available energy</v-label>
                            <p class="font-mono">{{ details.frozenFor.availableEnergy }}</p>
                        </div>
                        <div class="flex flex-col items-center gap-2">
                            <v-label>Available bandwidth</v-label>
                            <p class="font-mono">{{ details.frozenFor.availableBandwidth }}</p>
                        </div>
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                        <v-button color="green" type="button" @click="showStakeResourceModal = true">Stake</v-button>
                        <v-button color="red" type="button" @click="showUnStakeResourceModal = true">Un stake</v-button>
                    </div>
                </div>

                <v-modal :showing="showStakeResourceModal" @close="showStakeResourceModal = false">
                    <template v-slot:header>
                        Stake for Resource
                    </template>

                    <div class="flex flex-col gap-2">
                        <v-select label="resource" v-model="stakeResource.resource" :error="stakeResource.errors.resource">
                            <option v-for="res of resources" :value="res">{{ res }}</option>
                        </v-select>
                        <v-input
                            label="amount" type="number"
                            min="0" :max="parseInt(details.freeBalance)"
                            v-model="stakeResource.amount"
                            :error="stakeResource.errors.amount"
                        />
                    </div>

                    <template v-slot:footer>
                        <v-button type="button" @click="sendStakeResource">Confirm</v-button>
                    </template>
                </v-modal>

                <v-modal :showing="showUnStakeResourceModal" @close="showUnStakeResourceModal = false">
                    <template v-slot:header>
                        Un Stake Resource
                    </template>

                    <div class="flex flex-col gap-2">
                        <v-select label="resource" v-model="unStakeResource.resource" :error="unStakeResource.errors.resource">
                            <option v-for="res of resources" :value="res">{{ res }}</option>
                        </v-select>
                        <v-input
                            label="amount" type="number"
                            v-model="unStakeResource.amount"
                            :error="unStakeResource.errors.amount"
                        />
                    </div>

                    <template v-slot:footer>
                        <v-button type="button" @click="sendUnStakeResource">Confirm</v-button>
                    </template>
                </v-modal>

                <div class="flex flex-col border rounded bg-white gap-4 p-4">
                    <div class="flex flex-row justify-between items-baseline">
                        <p class="text-lg font-semibold">Votes</p>
                        <Link class="text-blue-700 hover:text-blue-600" :href="route('blockchain.staking.votes', [network, address])">Vote ></Link>
                    </div>

                    <div class="flex flex-row justify-between items-baseline hover:bg-gray-100">
                        <v-label>TRON Power</v-label>
                        <p class="font-bold">{{ details.frozenFor.tronPower }}</p>
                    </div>

                    <div class="flex flex-row justify-between items-baseline hover:bg-gray-100">
                        <v-label>Claimable TRX Rewards</v-label>
                        <p class="font-bold">{{ claimableReward }}</p>
                    </div>

                    <v-button :disabled="claimableReward === '0'" class="mt-auto" color="blue" type="button" @click="claim">Claim</v-button>
                </div>

                <div class="flex flex-col border rounded bg-white gap-4 p-4">
                    <div class="flex flex-row justify-between items-baseline">
                        <p class="text-lg font-semibold">Unfrozen</p>
                        <p class="text-2xl leading-6 font-mono">{{ details.UnfrozenBalance }}</p>
                    </div>

                    <div class="flex flex-col text-sm">
                        <div class="grid grid-cols-4 gap-2 font-bold">
                            <span class="col-span-2">Expire (UTC)</span>
                            <span>Type</span>
                            <span class="text-right">Amount</span>
                        </div>
                        <div class="grid grid-cols-4 border-t items-center gap-2" v-for="row of details.unfrozenDetails">
                            <span class="col-span-2">{{ row.expireTime }}</span>
                            <span>{{ row.type }}</span>
                            <span class="text-right font-mono">{{ row.amount }}</span>
                        </div>
                    </div>

                    <v-button class="mt-auto" type="button"
                            :disabled="!availableWithdrawUnfrozenBalance"
                            @click="withdrawUnfrozenBalance"
                    >Withdraw unfrozen balance</v-button>
                </div>

                <div class="col-span-2 flex flex-col border rounded bg-white gap-4 p-4">
                    <div class="flex flex-row justify-between items-baseline">
                        <p class="text-lg font-semibold">Delegation</p>
                        <div class="flex flex-row gap-4">
                            <v-button color="gray" type="button" @click="delegateResource.show = true">Delegate resource</v-button>
                            <v-button color="gray" type="button" @click="reclameResource.show = true">Reclaim resource</v-button>
                        </div>
                    </div>

                    <div class="flex flex-col text-sm">
                        <div class="grid grid-cols-3 gap-2 font-bold">
                            <span class="col-span-2">Address</span>
                            <span class="text-center">Energy / BW</span>
                        </div>
                        <div class="grid grid-cols-3 border-t items-center gap-2" v-for="(row, addr) of details.delegationDetails">
                            <span class="col-span-2">{{ addr }}</span>
                            <span class="text-center font-mono">{{ row.frozenBalanceForEnergy }} / {{ row.frozenBalanceForBW }}</span>
                        </div>
                    </div>
                </div>

                <v-modal :showing="delegateResource.show" @close="delegateResource.show = false">
                    <template v-slot:header>
                        Delegate resource
                    </template>

                    <div class="flex flex-col gap-2">
                        <v-input label="Address" v-model="delegateResource.address" :error="delegateResource.errors.address" />
                        <v-input label="Receiver" v-model="delegateResource.receiver" :error="delegateResource.errors.receiver" />
                        <div class="grid grid-cols-3 gap-4">
                            <v-input disabled label="Network" v-model="delegateResource.network" :error="delegateResource.errors.network" />
                            <v-select label="Resource" v-model="delegateResource.resource" :error="delegateResource.errors.resource">
                                <option v-for="res of resources" :value="res">{{ res }}</option>
                            </v-select>
                            <v-input label="Amount" type="number" v-model="delegateResource.amount" :error="delegateResource.errors.amount" />
                        </div>
                    </div>

                    <template v-slot:footer>
                        <v-button type="button" @click="sendResource(route('blockchain.staking.delegate-resource'), delegateResource)">Confirm</v-button>
                    </template>
                </v-modal>

                <v-modal :showing="reclameResource.show" @close="reclameResource.show = false">
                    <template v-slot:header>
                        Reclaim resource
                    </template>

                    <div class="flex flex-col gap-2">
                        <v-input label="Address" v-model="reclameResource.address" :error="reclameResource.errors.address" />
                        <v-input label="Receiver" v-model="reclameResource.receiver" :error="reclameResource.errors.receiver" />
                        <div class="grid grid-cols-3 gap-4">
                            <v-input disabled label="Network" v-model="reclameResource.network" :error="reclameResource.errors.network" />
                            <v-select label="Resource" v-model="reclameResource.resource" :error="reclameResource.errors.resource">
                                <option v-for="res of resources" :value="res">{{ res }}</option>
                            </v-select>
                            <v-input label="Amount" type="number" v-model="reclameResource.amount" :error="reclameResource.errors.amount" />
                        </div>
                    </div>

                    <template v-slot:footer>
                        <v-button type="button" @click="sendResource(route('blockchain.staking.reclaim-resource'), reclameResource)">Confirm</v-button>
                    </template>
                </v-modal>

            </div>


            <h2 class="text-2xl font-bold">Service Addresses</h2>

            <div class="bg-white border rounded p-4">
                <div class="grid grid-cols-6 p-2 gap-4 font-bold">
                    <span class="col-span-3">Id</span>
                    <span>Type</span>
                    <span>Token</span>
                    <span class="text-right">Amount</span>
                </div>
                <div class="grid grid-cols-6 border-t items-center p-2 gap-4" v-for="addr of addresses.addresses">
                    <span class="col-span-3 text-gray-400">{{ addr.id }}</span>
                    <span>{{ addr.type }}</span>
                    <span>{{ addr.currency }}</span>
                    <span class="text-right font-mono">{{ addr.balance.user }}</span>
                </div>
            </div>
        </div>
    </authenticated-layout>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3';
import AuthenticatedLayout from '@/Layouts/Authenticated';
import VLabel from '@/Components/Label';
import VInput from '@/Components/Input';
import VSelect from '@/Components/Select';
import VButton from '@/Components/Button';
import VModal from '@/Components/Modal';
import moment from 'moment';

export default {
    components: {
        Head, Link,
        AuthenticatedLayout,
        VLabel, VInput, VSelect, VButton, VModal,
    },
    props: {
        address: String,
        network: String,
        resources: Object,
        details: Object,
        addresses: Object,
        claimableReward: String,
        notifications: Array,
    },
    data(props) {
        return {
            'showStakeResourceModal': false,
            'showUnStakeResourceModal': false,
            'stakeResource': {
                'resource': 'energy',
                'amount': 0,
                'errors': {},
            },
            'unStakeResource': {
                'resource': 'energy',
                'amount': 0,
                'errors': {},
            },
            'delegateResource': {
                'address': props.address,
                'network': props.network,
                'receiver': '',
                'resource': 'energy',
                'amount': 0,
                'show': false,
                'errors': {},
            },
            'reclameResource': {
                'address': props.address,
                'network': props.network,
                'receiver': '',
                'resource': 'energy',
                'amount': 0,
                'show': false,
                'errors': {},
            },
            'stakingAnalysis': {
                start: new Date(new Date().setDate(new Date().getDate() - 1))
                    .toISOString()
                    .split('T')[0],
                end: new Date()
                    .toISOString()
                    .split('T')[0],
                result: {},
                show: false,
            },
        }
    },
    computed: {
        availableWithdrawUnfrozenBalance: (props) => props.details.unfrozenDetails.some(detail => moment(detail.expireTime).utc().isBefore(moment().utc())),
    },
    methods: {
        moment,
        sendStakeResource() {
            const { address, network } = this;
            const { resource, amount } = this.stakeResource;

            axios.post(this.route('blockchain.staking.stake-resource'), { address, network, resource, amount: `${amount}` })
                .then(res => {
                    this.stakeResource.amount  = 0;
                    this.showStakeResourceModal = false;
                    this.notifications.push(...res.data.notifications)
                })
                .catch(err => this.notifications.push({ type: 'error', title: `Error ${err.response.status}`, message: err.response.data }));
        },
        sendUnStakeResource() {
            const { address, network } = this;
            const { resource, amount } = this.unStakeResource;

            axios.post(this.route('blockchain.staking.un-stake-resource'), { address, network, resource, amount: `${amount}` })
                .then(res => {
                    this.unStakeResource.amount  = 0;
                    this.showUnStakeResourceModal = false;
                    this.notifications.push(...res.data.notifications)
                })
                .catch(err => this.notifications.push({ type: 'error', title: `Error ${err.response.status}`, message: err.response.data }));
        },
        withdrawUnfrozenBalance() {
            const { address, network } = this;

            axios.post(this.route('blockchain.staking.withdraw-unfrozen-balance'), { address, network })
                .then(res => this.notifications.push(...res.data.notifications))
                .catch(err => this.notifications.push({ type: 'error', title: `Error ${err.response.status}`, message: err.response.data }));
        },
        claim() {
            const { address, network } = this;

            axios.post(this.route('blockchain.staking.claim-voting-rewards'), { address, network })
                .then(res => this.notifications.push(...res.data.notifications))
                .catch(err => this.notifications.push({ type: 'error', title: `Error ${err.response.status}`, message: err.response.data }));
        },
        sendResource(route, param) {
            param.errors = {};
            const { address, network, receiver, resource, amount } = param;

            axios.post(route, { address, network, receiver, resource, amount: `${amount}` })
                .then(res => {
                    param.show = false;
                    param.address = '';
                    param.receiver = '';
                    param.amount  = 0;
                    this.notifications.push(...res.data.notifications)
                })
                .catch(err => {
                    if(err.response.status === 422) {
                        param.errors = err.response.data.errors;
                    } else this.notifications.push({ type: 'error', title: `Error ${err.response.status}`, message: err.response.data })
                });
        },
        getStakingAnalysis() {
            const { address, network } = this;
            const { start, end } = this.stakingAnalysis;

            axios.post(this.route('blockchain.staking.get-staking-analysis'), { address, network, start, end })
                .then(res => {
                    this.stakingAnalysis.show = res.data.success;
                    console.log(res.data.result)
                    this.stakingAnalysis.result = res.data.result;
                    this.notifications.push(...res.data.notifications)
                })
                .catch(err => {
                    this.notifications.push({ type: 'error', title: `Error ${err.response.status}`, message: err.response.data })
                });
        },
    },
};
</script>
